import type { PassengerNameProps } from "@apac/checkout";
import { usePersonNameFormat } from "@apac/hooks";
import type { Person as FlightPerson } from "@b2bportal/air-booking-api";
import { useModuleBEM } from "@b2bportal/core-themes";
import type { Person as CarPerson } from "@b2bportal/ground-api";
import type { Person as HotelPerson } from "@b2bportal/lodging-api";
import type { Traveler } from "@b2bportal/traveler-api";
import { useI18nContext } from "@hopper-b2b/i18n";
import { TravelProductEnum } from "@hopper-b2b/types";
import clsx from "clsx";
import { useInfantInfo } from "./hooks/useInfantInfo";

type Person = FlightPerson | HotelPerson | CarPerson;

const isTraveler = (input: unknown): input is Traveler => {
  return (
    input != null &&
    typeof input === "object" &&
    (input as Traveler).name != null
  );
};

export const PassengerName = ({
  className,
  passenger,
  funnell,
}: Omit<PassengerNameProps, "passenger"> & {
  passenger: Traveler | Person;
}) => {
  const formatName = usePersonNameFormat();
  const [block, cn] = useModuleBEM({}, "PassengerName");

  const name = isTraveler(passenger)
    ? formatName({
        givenName: passenger.name?.first,
        surname: passenger.name?.last,
        middleName: passenger.name?.middle,
      } as Person)
    : formatName(passenger);

  return (
    <div
      id={`passenger-${passenger.id}-name`}
      className={clsx(className, block)}
    >
      {name}

      {/* TODO: This is kinda messy, proper fix is to decouple below component from Checkout X-State machine */}
      {funnell === TravelProductEnum.Flights && (
        <InfantInfo
          className={cn("infant-info")}
          passenger={passenger as Traveler}
        />
      )}
    </div>
  );
};

const InfantInfo = ({
  passenger,
  className,
}: {
  passenger: Traveler;
  className: string;
}) => {
  const { t } = useI18nContext();
  const { isInfant, seatType } = useInfantInfo({
    passenger,
  });
  if (!(isInfant && seatType != null)) {
    return null;
  }

  return (
    <div className={className}>
      (
      {seatType === "OnLap"
        ? t("checkoutSection.travelerForm.infant.onLap")
        : t("checkoutSection.travelerForm.infant.ownSeat")}
      )
    </div>
  );
};
